import { apiFetch } from '../../stores/authStore';

export interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  username: string;
  role: string;
  orgId: string | null;
  createdAt: string;
}

export type AdminsRead =
  | { kind: 'ok'; admins: AdminUser[] }
  | { kind: 'failed'; error: string }
  /** A newer read started, or the signed-in admin changed — not a failure. */
  | { kind: 'superseded' };

// The Admin page's last confirmed lists, each stamped with the admin who read
// it, so a revisit by the same admin paints them at once (marked while they
// re-read) without the page shifting, and nobody else ever sees them.
type Stamped<T> = { identity: string; rows: T[] } | null;
let _admins: Stamped<AdminUser> = null;
let _orgs: Stamped<AdminOrg> = null;
let _readSeq = 0;

const rowsFor = <T,>(entry: Stamped<T>, identity: string | null): T[] | null =>
  identity !== null && entry?.identity === identity ? entry.rows : null;
const stamp = <T,>(identity: string | null, rows: T[]): Stamped<T> =>
  identity !== null ? { identity, rows } : null;

export function cachedAdmins(identity: string | null): AdminUser[] | null {
  return rowsFor(_admins, identity);
}

export function rememberAdmins(identity: string | null, admins: AdminUser[]): void {
  _admins = stamp(identity, admins);
}

export function cachedOrgs(identity: string | null): AdminOrg[] | null {
  return rowsFor(_orgs, identity);
}

export function rememberOrgs(identity: string | null, orgs: AdminOrg[]): void {
  _orgs = stamp(identity, orgs);
}

/** Read `/api/admin/admins` for `identity`. Only the latest read lands, and only
 *  while `currentIdentity()` is still the admin it was started for; a landed
 *  success is remembered for that admin. Never throws. */
export async function readAdmins(
  identity: string | null,
  currentIdentity: () => string | null,
): Promise<AdminsRead> {
  const seq = ++_readSeq;
  const landed = () => seq === _readSeq && currentIdentity() === identity;
  let res: Response;
  let data: { admins?: unknown; error?: unknown };
  try {
    res = await apiFetch('/api/admin/admins');
    const body: unknown = await res.json().catch(() => ({}));
    data = body !== null && typeof body === 'object' ? body : {};
  } catch {
    return landed() ? { kind: 'failed', error: 'Could not reach the server' } : { kind: 'superseded' };
  }
  if (!landed()) return { kind: 'superseded' };
  // Shown as text: anything but a string falls back rather than reaching JSX.
  if (!res.ok) return { kind: 'failed', error: typeof data.error === 'string' ? data.error : `HTTP ${res.status}` };
  if (!Array.isArray(data.admins) || !data.admins.every((row) =>
    row !== null && typeof row === 'object' &&
    typeof row.id === 'string' && typeof row.username === 'string' &&
    typeof row.role === 'string' &&
    (row.orgId === null || typeof row.orgId === 'string') &&
    typeof row.createdAt === 'string'
  )) return { kind: 'failed', error: 'Unexpected response' };
  const admins = data.admins as AdminUser[];
  rememberAdmins(identity, admins);
  return { kind: 'ok', admins };
}

/** Sign-out / identity replacement. Memory only: both lists are already
 *  identity-checked on every read, and in-flight reads fence themselves. */
export function resetAdminPageCache(): void {
  _admins = null;
  _orgs = null;
}
